/**
 * Active transactions — NIT-010 prominence.
 *
 * When ≥1 tx is active the card flips to an emerald-tinted scheme with
 * a live-pulse dot in the header (matches evse2's renderActiveTransactions
 * "live" indicator). Otherwise stays in the muted slate palette.
 */
import { useStationStore } from '@/state/useStationStore';

export function ActiveTransactionsList(): JSX.Element {
  const connectors = useStationStore((s) => s.connectors);
  const active = [...connectors.values()].filter((c) => c.activeTransactionId !== null);
  const hasActive = active.length > 0;

  return (
    <section
      className={
        hasActive
          ? 'rounded border border-primary-base bg-primary-base/30 p-4 shadow-[0_0_0_1px_rgb(16_185_129_/_15%)]'
          : 'rounded border border-slate-700 bg-slate-900/60 p-4'
      }
      aria-label="Active transactions"
      data-testid="active-tx-card"
    >
      <header className="mb-2 flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          {hasActive ? (
            <span
              className="inline-block h-2 w-2 animate-pulse rounded-full bg-primary-base"
              aria-hidden="true"
              data-testid="active-tx-pulse"
            />
          ) : null}
          <h2 className={`font-medium ${hasActive ? 'text-primary-on' : 'text-slate-100'}`}>
            Active transactions
          </h2>
        </div>
        <span
          className={`text-xs ${hasActive ? 'text-primary-soft' : 'text-slate-400'}`}
          data-testid="active-tx-count"
        >
          {active.length}
        </span>
      </header>
      {active.length === 0 ? (
        <p className="text-xs text-slate-500" data-testid="no-active-tx">
          No active transactions.
        </p>
      ) : (
        <ul className="space-y-1 text-xs text-primary-on">
          {active.map((c) => (
            <li key={c.id} data-testid={`active-tx-${c.id}`}>
              Connector {c.id} · tx#{String(c.activeTransactionId)} · {c.activeIdTag}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
